import { useMediaQuery, useTheme } from '@mui/material';
import {
  CcfBox,
  CcfTypography,
  CcfButton,
  useTranslator,
  CcfIconButton,
  CcfDeleteIcon,
} from '@nice-ccf/ui-controls';
import ccfCommitmentStyles from './ccf-commitment-form.styles';
import { useRemoveCommitment } from './ccf-remove-commitment';
import {
  commitmentActions,
  CommitmentFormState,
  getRemoveCommitmentSettings,
  getCommitmentPermission,
  ScheduledCommitment
} from './ccf-commitment.slice';
import { useDispatch, useSelector } from 'react-redux';
import { getUtcTimezoneOffset } from '../../util/common';
import dayjs from 'dayjs';
import {
  getPanelAppNavigationItems,
  getSelectedMenuName,
  globalActions,
} from '../global.app.slice';
import { Navigation } from '../../enums/navigation-menus';
import { updateAppSpaceTab } from '../ccf-inbox/ccf-inbox.slice';

/**
 * Notification for commitments
 * @example <CcfReceiveCommitment />
 */
export const CcfReceiveCommitment = (props: {
  commitmentInfo: ScheduledCommitment;
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const styles = ccfCommitmentStyles(theme);
  const [translate] = useTranslator();
  const notesRequiredToDelete = useSelector(getRemoveCommitmentSettings);
  const { delete: canDelete } = useSelector(getCommitmentPermission);
  const panelAppNavigationItems = useSelector(getPanelAppNavigationItems);
  const selectedMenuItem = useSelector(getSelectedMenuName);
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));

  const {
    contactId,
    targetType,
    skillOrQueueId,
    firstName,
    lastName,
    notes,
    contactMode,
    contactReceivedTime,
  } = props.commitmentInfo;

  const { triggerRemoveToast } = useRemoveCommitment(
    notesRequiredToDelete,
    Number(contactId)
  );

  /**
   * Function to handle call
   * @example handleCall()
   * @returns
   */
  const handleCall = async () => {
    //dispatch(makeCommitmentCall(contactId));
    console.log("make commitment");
  };

  /**
   * Function to cancel call
   * @example handleCancelCall()
   * @returns
   */
  const handleCancelCall = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event?.stopPropagation();
    triggerRemoveToast();
  };

  /**
   * Function to open reschedule commitment form
   * @example openRescheduleCommitmentForm()
   * @returns
   */
  const openRescheduleCommitmentForm = async () => {
    if (isSmView) {
      await dispatch(
        globalActions.setSelectedMenu({ name: Navigation.CALENDER })
      );
    }
    const commitmentData = {
      commitmentType:
        targetType === 'A' ? translate('agent') : translate('skill'),
      skillId: String(skillOrQueueId),
      fname: firstName,
      lname: lastName,
      phone: contactMode,
      timeZone: getUtcTimezoneOffset(),
      dateTime: dayjs(new Date(contactReceivedTime))
        .add(1, 'hour')
        .format('YYYY-MM-DDTHH:mm:ss'),
      notes: notes,
      callbackId: String(contactId),
    };

    await dispatch(commitmentActions.setIsRescheduleCommitment(true));
    await dispatch(
      commitmentActions.editCommitmentEvent(
        commitmentData as CommitmentFormState
      )
    );
    await dispatch(commitmentActions.showCommitmentForm(true));
  };
  /**
   * Function to reschedule call
   * @example handleRescheduleCall()
   * @returns
   */
  const handleRescheduleCall = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event?.stopPropagation();

    if (selectedMenuItem === Navigation.CALENDER) {
      await openRescheduleCommitmentForm();
    } else {
      await dispatch(
        globalActions.setSelectedMenu({ name: Navigation.ASSIGNMENT })
      );
      const activeTabApp = panelAppNavigationItems.find(
        (tab : any) => tab.menuName === Navigation.CALENDER
      );
      await dispatch(
        updateAppSpaceTab({
          tab: activeTabApp?.menuName || '',
        })
      );

      await openRescheduleCommitmentForm();
    }
  };

  return (
    <CcfBox>
      <CcfTypography variant="inherit" sx={styles.commitmentNotes}>
        {notes}
      </CcfTypography>

      <CcfBox sx={styles.commitmentControls} data-testid="CommitmentDetail">
        <CcfButton
          sx={styles.commitmentControlButton}
          onClick={handleRescheduleCall}
          data-testid="reschedule"
        >
          <CcfTypography translationKey="Reschedule" />
        </CcfButton>
        <CcfButton
          sx={styles.commitmentControlButton}
          primary
          onClick={handleCall}
          data-testid="proceed"
        >
          <CcfTypography translationKey="Proceed" />
        </CcfButton>
        {canDelete && (
          <CcfIconButton
            sx={styles.deleteIconButton}
            onClick={handleCancelCall}
            data-testid="remove-commitment"
          >
            <CcfDeleteIcon />
          </CcfIconButton>
        )}
      </CcfBox>
    </CcfBox>
  );
};
